import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AudioPlayer from '@/trio-ui/AudioPlayer';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import * as Styles from './InterviewQuestion.styles';

type InterviewQuestionComponentProps = {
  id: string;
  label: string;
  questionTitle: string;
  answer?: string;
  isEditing: boolean;
  canEdit?: boolean;
  onChangeQuestion: (event: any) => void;
  onTogglingEdit: () => void;
  onSave: () => void;
  onDelete: () => void;
};

export default function InterviewQuestionComponent({
  id,
  label,
  answer,
  isEditing,
  canEdit,
  onChangeQuestion,
  onTogglingEdit,
  onSave,
  onDelete,
  questionTitle,
}: InterviewQuestionComponentProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);

  const firstButton = {
    label: isEditing ? 'Save' : 'Edit',
    icon: isEditing ? SaveIcon : EditIcon,
    action: isEditing ? onSave : onTogglingEdit,
  };

  const secondButton = {
    label: isEditing ? 'Cancel' : 'Delete',
    icon: isEditing ? CancelIcon : DeleteIcon,
    action: isEditing ? onTogglingEdit : onDelete,
  };

  const isNewQuestion = useMemo(() => !id, [id]);
  const [generateQuestionByAI, setGenerateQuestionByAI] = useState<
    'Cultural' | 'Technical' | 'Soft Skills' | ''
  >('');

  const handleChangeQuestionByAI = (event: any): void => {
    const value = event.target.value === 'Select an option' ? '' : event.target.value;
    setGenerateQuestionByAI(value);
    onChangeQuestion({ target: { value: label, generateQuestionByAI: value } });
  };

  useEffect(() => {
    if (!isEditing) {
      setIsLoading(false);
    }
  }, [isEditing]);

  return (
    <Styles.Box>
      <Styles.QuestionNumberLabel>{questionTitle}</Styles.QuestionNumberLabel>

      {isEditing ? (
        <Styles.QuestionInput>
          <InputText
            name="question-label"
            label=""
            onChangeHandler={(e) =>
              onChangeQuestion({ ...e, target: { value: e.target.value, generateQuestionByAI } })
            }
            value={label}
            marginBottom={2}
          />
          {isNewQuestion && (
            <DropDown
              name="question-by-ai"
              label="Generate question using AI"
              isLabelAboveField
              defaultValue={generateQuestionByAI}
              onChangeDropDown={handleChangeQuestionByAI}
              options={['Select an option', 'Cultural', 'Technical', 'Soft Skills']}
            />
          )}
        </Styles.QuestionInput>
      ) : (
        <Styles.QuestionLabel>{label}</Styles.QuestionLabel>
      )}
      {!!answer && <AudioPlayer extraId={id} audioUrl={answer} />}

      {canEdit && (
        <Styles.Footer>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <TransparentButton
                onClick={() => {
                  if (isEditing) setIsLoading(true);
                  firstButton.action();
                }}
                icon={firstButton.icon}
                disabled={isNewQuestion ? !label && !generateQuestionByAI : !label}
                isBlueButton={isEditing}
              >
                {firstButton.label}
              </TransparentButton>
              <TransparentButton
                onClick={(event) => {
                  event.stopPropagation();
                  secondButton.action();
                }}
                icon={secondButton.icon}
                isRedButton
              >
                {secondButton.label}
              </TransparentButton>
            </>
          )}
        </Styles.Footer>
      )}
    </Styles.Box>
  );
}
